'use client';

import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
import { ProductCarousel, Slide } from '@/components/molecules/ProductCarousel';
import { ProductCard } from '@/components/molecules/ProductCard';
import { useBestSellerProducts } from '@/lib/hooks';
import { useTranslation } from '@/lib/i18n';
import { useIntl } from '@/lib/intl';
import { LazySection } from '@/components/atoms/LazySection';

const Section = styled.section`
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function BestSellersContent() {
  const theme = useTheme();
  const { products, loading, error } = useBestSellerProducts();
  const { t } = useTranslation();
  const { localizedPath } = useIntl();

  if (!loading && error) {
    return (
      <Section theme={theme}>
        <ErrorMessage theme={theme}>
          Unable to load best sellers. Please try again later.
        </ErrorMessage>
      </Section>
    );
  }

  if (!loading && products.length === 0) return null;

  return (
    <Section theme={theme}>
      <ProductCarousel
        title={t('home.bestSellers')}
        seeAllPath={localizedPath('/collections/best-sellers')}
        seeAllLabel={t('home.seeAll')}
        isLoading={loading}
        skeletonCount={4}
        ariaLabel={t('home.bestSellers')}
      >
        {products.map((product) => (
          <Slide key={product.id} role="listitem">
            <ProductCard
              id={product.id}
              slug={product.slug}
              name={product.name}
              image={product.image}
              price={product.price}
              compareAtPrice={product.compareAtPrice}
              rating={product.rating}
              reviewCount={product.reviewCount}
              bottomBadge="Best Seller"
              bottomBadgeType="bestseller"
            />
          </Slide>
        ))}
      </ProductCarousel>
    </Section>
  );
}

export function BestSellers() {
  return (
    <LazySection minHeight="420px" ariaLabel="Best sellers section loading">
      <BestSellersContent />
    </LazySection>
  );
}
